import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { DeviceEventEmitter, Linking } from "react-native";

import { makeUrl } from "../globalUtils";
import EventPersonalizeUploadCardUI from "./EventPersonalizeUploadCardUI";
import { videoUpload } from "./EventPersonalizeUploadUtils";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import { t } from "@/localization";
import UploadProgressCard from "../uploadV2/UploadProgressCard";
import {
  UPLOAD_V2_FILE_HINT,
  isUploadBusy,
  uploadErrorText,
  uploadPanelView,
} from "../uploadV2/presentation";
import { useUploadV2 } from "../uploadV2/useUploadV2";

const HANDED_OVER = new Set(["queued", "converting", "done"]);

const EventPersonalizeUploadCardWeb = ({
  event,
  onComplete,
  onDeletePress,
  notifyGuests,
  uploadButtonText,
  forceShowUploadForm,
  onUploadStart,
  onUploadError,
  uploadMode,
  insertIndex,
  uploadMeta,
}) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0.0);
  const [loaded, setLoaded] = useState(0);
  const [total, setTotal] = useState(0);
  const { showActionSheetWithOptions } = useActionSheet();

  // Upload Service v2 (EXPO_PUBLIC_UPLOAD_V2): evt.live queues the conversion
  // itself, so this path never calls onComplete/convertVideo.
  const uploadV2 = useUploadV2(event?.slug);
  const v2 = uploadV2.available;
  const v2Status = uploadV2.state.status;
  const { start: startV2, getState: getV2State } = uploadV2;
  const previousV2Status = useRef(v2Status);

  useEffect(() => {
    const previous = previousV2Status.current;
    previousV2Status.current = v2Status;
    if (
      v2 &&
      (previous === "uploading" || previous === "completing") &&
      HANDED_OVER.has(v2Status)
    ) {
      DeviceEventEmitter.emit("upload-message", {
        message: t("Event video is uploaded and will be processed now"),
      });
    }
  }, [v2, v2Status]);

  const onRequestProgress = useCallback((progressEvent) => {
    console.log("PROGRESS EVENT:", progressEvent);
    if (progressEvent.progress) {
      setProgress(progressEvent.progress);
    } else {
      setLoaded(progressEvent.transferredBytes);
      setTotal(progressEvent.totalBytes);
      setProgress(progressEvent.transferredBytes / progressEvent.totalBytes);
    }
  });

  const { slug, stream_key } = event;

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length) return;

      if (v2) {
        if (isUploadBusy(getV2State())) return;
        if (typeof onUploadStart === "function") onUploadStart();
        startV2({
          file: acceptedFiles[0],
          mode: uploadMode,
          insertIndex,
          notifyGuests,
          meta: uploadMeta,
          event,
        });
        return;
      }

      setLoading(true);
      const file = acceptedFiles[0];

      if (typeof onUploadStart === "function") onUploadStart();

      try {
        const vod = await videoUpload({
          slug,
          file,
          notifyGuests,
          stream_key,
          onUploadProgress: onRequestProgress,
        });

        if (typeof onComplete === "function") onComplete({ vod });
      } catch (error) {
        console.error(error);
        if (typeof onUploadError === "function") onUploadError(error);
      } finally {
        setLoading(false);
      }
    },
    [
      notifyGuests,
      slug,
      stream_key,
      onComplete,
      onUploadError,
      onUploadStart,
      v2,
      startV2,
      getV2State,
      uploadMode,
      insertIndex,
      uploadMeta,
      event,
    ]
  );

  //TODO: show error when dropped file rejected
  const onDropRejected = useCallback((rejectedFiles) => {
    DeviceEventEmitter.emit("upload-message", {
      type: "error",
      message: t("Only mov or mp4 files are accepted"),
      //rejectedFiles[0].errors[0].message,
    });
  }, []);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    noDragEventsBubbling: false,
    multiple: false,
    accept: "video/*",
  });

  let v2Props = null;
  if (v2) {
    // The event page, for "View event page" once the video is ready.
    const eventUrl =
      event?.alias || (event?.broadcaster && event?.slug)
        ? makeUrl(event)
        : null;
    const panel = uploadPanelView(uploadV2.state, { eventUrl });
    const confirmCancel = () =>
      showActionSheetWithOptions(
        {
          options: [t("Cancel upload"), t("Keep uploading")],
          cancelButtonIndex: 1,
          destructiveButtonIndex: 0,
          title: t("Cancel this upload?"),
          message: t("The part already uploaded will be discarded."),
        },
        (buttonIndex) => {
          if (buttonIndex === 0) uploadV2.cancel();
        }
      );
    const onAction = (key) => {
      if (key === "pause") uploadV2.pause();
      else if (key === "resume" || key === "retry") uploadV2.resume();
      else if (key === "uploadAgain") uploadV2.uploadAgain();
      else if (key === "viewEvent" && eventUrl) Linking.openURL(eventUrl);
      else if (key === "close") uploadV2.dismiss();
      else if (key === "cancel") {
        // Nothing was sent for a duplicate, so no need to confirm.
        if (uploadV2.state.status === "duplicate") uploadV2.cancel();
        else confirmCancel();
      }
    };
    v2Props = {
      // Every v2 state (uploading to converting) shows in the same fixed-size
      // card; the regular prompt and video show otherwise.
      uploadPanel: panel ? (
        <UploadProgressCard {...panel} onAction={onAction} />
      ) : undefined,
      errorText: uploadErrorText(uploadV2.state) || undefined,
      fileHint: UPLOAD_V2_FILE_HINT,
      onChooseFile: isUploadBusy(uploadV2.state) ? undefined : open,
    };
  }

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <EventPersonalizeUploadCardUI
        event={event}
        loading={loading}
        progress={progress}
        total={total}
        loaded={loaded}
        isVideo={true}
        highlighted={isDragActive}
        uploadButtonText={uploadButtonText}
        forceShowUploadForm={forceShowUploadForm}
        onChooseFile={open}
        onDeletePress={onDeletePress}
        {...v2Props}
      />
    </div>
  );
};

export default EventPersonalizeUploadCardWeb;
